import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, formatDate, statusTone } from "@/lib/format";
import ProjectExtraFields, { formatNoteTs } from "@/components/app/ProjectExtraFields";
import { StatusBadge } from "@/lib/statusColor";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Trash2, Send, UploadCloud, Link2, Megaphone, IndianRupee, Pencil, Shield, EyeOff, Eye, Lock, Ticket, Search } from "lucide-react";
import PhaseTracker from "@/components/app/PhaseTracker";
import ClientTicketsPanel from "@/components/app/ClientTicketsPanel";
import ClientApprovalPanel, { ApprovalBadge } from "@/components/app/ClientApprovalPanel";
import ProjectHistoryPanel from "@/components/app/ProjectHistoryPanel";
import WoPaymentDialog from "@/components/app/WoPaymentDialog";
import WoDetailDialog from "@/components/app/WoDetailDialog";
import RichEditor from "@/components/app/RichEditor";
import RichText from "@/components/app/RichText";
import { plainToHtml, isRichEmpty } from "@/lib/richtext";

const FREELANCER_STATUSES = ["Assigned", "In Progress", "Submitted"];
const STATUSES = ["Assigned", "In Progress", "Submitted", "Completed", "Reassigned", "Cancelled"];
const fmtCreated = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function bytesLabel(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const isManager = ["Manager", "TeamLeader", "SuperAdmin"].includes(user.role);
  const isFreelancer = user.role === "Freelancer";
  const [project, setProject] = useState(null);
  const [orders, setOrders] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [clientUpdates, setClientUpdates] = useState([]);
  const [newClientUpdate, setNewClientUpdate] = useState("");
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [spend, setSpend] = useState({ paid: 0, invoiced: 0, invoices: 0, breakdown: [] });
  const [editing, setEditing] = useState(null);
  const [editingWo, setEditingWo] = useState(null);
  const [deletingWo, setDeletingWo] = useState(null);
  const [invoiceMap, setInvoiceMap] = useState({});
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({ freelancer_id: "", description: "", instructions: "", deadline: "" });
  const [ticketSearch, setTicketSearch] = useState("");
  const [woSearch, setWoSearch] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [paymentFor, setPaymentFor] = useState(null);
  const [viewWoId, setViewWoId] = useState(null);
  const chatRef = useRef(null);
  const [woSaving, setWoSaving] = useState(false);
  const [ticketResponse, setTicketResponse] = useState({});

  // Freelancers assigned to this project via Work Orders (not every freelancer in the system)
  const projectFreelancerIds = Array.from(new Set((orders || []).map((o) => o.freelancer_id).filter(Boolean)));
  const projectFreelancers = freelancers.filter((f) => projectFreelancerIds.includes(f.id))
    .filter((f) => !ticketSearch || (f.name || "").toLowerCase().includes(ticketSearch.toLowerCase()));
  const woFreelancers = freelancers.filter((f) =>
    !woSearch || (f.name || "").toLowerCase().includes(woSearch.toLowerCase()) || (f.email || "").toLowerCase().includes(woSearch.toLowerCase()));

  const loadTickets = async () => {
    try { const { data } = await api.get(`/tickets?project_id=${id}`); setTickets(data); } catch {}
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", { project_id: id, ...ticketForm });
      setTicketOpen(false); setTicketForm({ freelancer_id: "", description: "", instructions: "", deadline: "" });
      toast.success("Ticket created and freelancer notified");
      loadTickets();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateTicket = async (t, patch) => {
    try { await api.patch(`/tickets/${t.id}`, patch); loadTickets(); toast.success("Ticket updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const [woOpen, setWoOpen] = useState(false);
  const [woForm, setWoForm] = useState({ freelancer_id: "", title: "", description: "", rate: 0, deadline: "", status: "Assigned" });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ name: "", url: "" });
  const [services, setServices] = useState([]);
  const [phaseEditor, setPhaseEditor] = useState(null); // array of phases while editing
  const [editingVendors, setEditingVendors] = useState([]);
  const [editingSmes, setEditingSmes] = useState([]);
  const [clientDetailsOpen, setClientDetailsOpen] = useState(false);
  const [clientDetailsDraft, setClientDetailsDraft] = useState({ name: "", phone: "", email: "", department: "", topic: "" });
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const [p, wo, c] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/work-orders?project_id=${id}`),
      api.get(`/projects/${id}/comments`),
    ]);
    setProject(p.data); setOrders(wo.data); setComments(c.data);
    setNotesDraft("");
    // Load invoice mapping so delete confirmations show what will be cascaded.
    if (isManager) {
      try {
        const pay = await api.get("/payments");
        const m = {}; pay.data.forEach((row) => { if (row.work_order_id) m[row.work_order_id] = row; });
        setInvoiceMap(m);
      } catch {}
    }
    if (isManager) { const f = await api.get("/freelancers"); setFreelancers(f.data); }
    if (isManager) { try { const s = await api.get("/services"); setServices(s.data); } catch { setServices([]); } }
    if (isManager) {
      try {
        const [vd, sm] = await Promise.all([api.get("/vendors"), api.get("/smes")]);
        setEditingVendors(vd.data); setEditingSmes(sm.data);
      } catch {}
    }
    if (isSuperAdmin) {
      try {
        const u = await api.get("/users");
        setManagers(u.data.filter((x) => ["Manager", "TeamLeader", "SuperAdmin"].includes(x.role) && x.status !== "deactivated"));
      } catch { setManagers([]); }
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (id) loadTickets(); }, [id]);

  // Deep-link: /projects/:id?edit_wo=<wid> opens the Edit dialog with that WO pre-filled.
  useEffect(() => {
    const wid = searchParams.get("edit_wo");
    if (!wid || !orders?.length) return;
    const wo = orders.find((o) => o.id === wid);
    if (wo) { setEditingWo({ ...wo }); }
    // Clear the query so refresh doesn't re-open it.
    const next = new URLSearchParams(searchParams); next.delete("edit_wo"); setSearchParams(next, { replace: true });
  }, [orders, searchParams, setSearchParams]);

  const toggleFileHidden = async (f) => {
    try {
      await api.patch(`/projects/${id}/files/${f.id}/hide?hide=${!f.hidden}`);
      toast.success(f.hidden ? "File visible again" : "File hidden from freelancers & clients");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveNotes = async () => {
    const text = (notesDraft || "").trim();
    if (!text) return;
    setSavingNotes(true);
    try {
      await api.post(`/projects/${id}/notes`, { text });
      setNotesDraft("");
      toast.success("Note added");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingNotes(false);
  };

  const deleteNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/projects/${id}/notes/${nid}`); toast.success("Note deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveClientDetails = async (details) => {
    try {
      await api.patch(`/projects/${id}`, { client_details: details });
      toast.success("Client details saved");
      setClientDetailsOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmDeleteWo = async () => {
    if (!deletingWo) return;
    try {
      const { data } = await api.delete(`/work-orders/${deletingWo.id}`);
      const extra = data?.invoices_deleted ? ` (${data.invoices_deleted} invoice removed)` : "";
      toast.success(`Work order deleted${extra}`);
      setDeletingWo(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!project) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  const spent = orders.reduce((sum, o) => sum + (o.status === "Completed" ? o.rate : 0), 0);
  const budgetPct = project.budget > 0 ? Math.min(100, Math.round((spent / project.budget) * 100)) : 0;
  const fname = (fid) => freelancers.find((x) => x.id === fid)?.name || (user.linked_freelancer_id === fid ? user.name : "—");

  const openConvertToWo = () => {
    const defaultFreelancer = project.assigned_freelancer_ids?.[0] || "";
    setWoForm({ freelancer_id: defaultFreelancer, title: "", description: "", rate: 0, deadline: project.due_date || "", status: "Assigned" });
    setWoSearch("");
    setWoOpen(true);
  };

  const submitWo = async (e) => {
    e.preventDefault();
    if (woForm.deadline && project.due_date && woForm.deadline > project.due_date) {
      toast.error("Work order due date must be within the project timeline"); return;
    }
    try {
      await api.post("/work-orders", { ...woForm, rate: Number(woForm.rate), project_id: id });
      setWoOpen(false); load(); toast.success("Work order created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitEditWo = async (e) => {
    e.preventDefault();
    if (editingWo.deadline && project.due_date && editingWo.deadline > project.due_date) {
      toast.error("Work order due date must be within the project timeline"); return;
    }
    if (woSaving) return;
    setWoSaving(true);
    try {
      const payload = {
        title: editingWo.title, description_html: editingWo.description_html ?? plainToHtml(editingWo.description),
        rate: Number(editingWo.rate), deadline: editingWo.deadline,
        status: editingWo.status, freelancer_id: editingWo.freelancer_id,
      };
      const prev = orders.find((o) => o.id === editingWo.id);
      const { data } = await api.patch(`/work-orders/${editingWo.id}`, payload);
      const justCompleted = payload.status === "Completed" && prev?.status !== "Completed";
      setEditingWo(null); load();
      if (justCompleted && isManager) setPaymentFor({ ...editingWo, ...(data || {}), rate: payload.rate });
      else toast.success("Work order updated");
    } catch (err) { toast.error(formatApiError(err)); }
    setWoSaving(false);
  };

  const submitEditProject = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, budget: Number(editing.budget), backup_manager_id: editing.backup_manager_id || null, vendor_id: editing.vendor_id || null, sme_id: editing.sme_id || null };
      delete payload.id; delete payload.created_at; delete payload.files; delete payload.updated_at;
      await api.patch(`/projects/${id}`, payload);
      setEditing(null); load(); toast.success("Project updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyServiceTemplate = (serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    if (!svc) return;
    setEditing((cur) => ({ ...cur, service_id: serviceId, phases: (svc.phases || []).map((p) => ({ name: p.name, days: p.days })) }));
  };

  const deleteProject = async () => {
    if (!window.confirm(`Delete project "${project.name}"? This can't be undone.`)) return;
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); window.location.href = "/projects"; }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      try {
        await api.post(`/projects/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) { fail++; toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setUploading(false);
    if (ok > 0) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
    if (ok > 0 || fail > 0) load();
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const addLink = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/files`, linkForm);
      setLinkForm({ name: "", url: "" }); setLinkOpen(false); load();
      toast.success("Link attached");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeFile = async (fileId) => {
    if (!window.confirm("Remove attachment?")) return;
    await api.delete(`/projects/${id}/files/${fileId}`); load();
  };

  const submitComment = async (e) => {
    e?.preventDefault();
    if (isRichEmpty(newComment)) return;
    try {
      await api.post(`/projects/${id}/comments`, { body_html: newComment });
      chatRef.current?.clear(); setNewComment(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const postClientUpdate = async () => {
    if (!newClientUpdate.trim()) { toast.error("Write an update first"); return; }
    setPostingUpdate(true);
    try {
      await api.post(`/projects/${id}/client-updates`, { body: newClientUpdate.trim() });
      setNewClientUpdate("");
      await load();
      toast.success("Update posted to the client portal.");
    } catch (err) { toast.error(formatApiError(err)); }
    setPostingUpdate(false);
  };

  const fileHref = (f) => {
    if (f.url) return f.url;
    // download route needs auth — construct URL, will fetch via axios below
    return null;
  };

  const downloadStored = async (f) => {
    try {
      const resp = await api.get(`/projects/${id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="projects-module">
      <PageHeader
        title={<span className="inline-flex items-center gap-3 flex-wrap">{project.name}<ApprovalBadge status={project.client_approval_status} testid="pd-header-approval-badge" /></span>}
        description={<><Link to="/projects" className="underline decoration-2 underline-offset-4">← Projects</Link> · <span className="text-neutral-600">{project.client_name}</span></>}
        actions={isManager && (
          <>
            <button data-testid="edit-project-btn" onClick={() => setEditing({ ...project })} className="border-2 border-black bg-white px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Pencil size={14} /> Edit</button>
            <button data-testid="delete-project-btn" onClick={deleteProject} className="border-2 border-[#B02A00] text-[#B02A00] bg-white px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Trash2 size={14} /> Delete</button>
            <button data-testid="attach-link-btn" onClick={() => setLinkOpen(true)} className="border-2 border-black bg-white px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Link2 size={14} /> Add Link</button>
            <button data-testid="create-ticket-btn" onClick={() => setTicketOpen(true)} className="border-2 border-royal-700 bg-white text-royal-800 px-3 py-2 text-sm hard-shadow-hover inline-flex items-center gap-2"><Ticket size={14} /> Create Ticket</button>
            <button data-testid="convert-to-wo-btn" onClick={openConvertToWo} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">+ Work Order</button>
          </>
        )}
      />
      <div className="px-8 pt-6">
        <PhaseTracker phases={project.phases || []} projectDueDate={project.due_date} projectStatus={project.status} testid="internal-phase-tracker" />
      </div>
      <div className="p-8 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-neutral-200 bg-white p-6 lg:col-span-1 flex flex-col" style={{ maxHeight: "640px" }}>
          <div className="label-caps shrink-0">Overview</div>
          <div className="mt-3 flex-1 overflow-auto pr-1" data-testid="overview-scroll">
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-neutral-500">Status</div><div><StatusBadge status={project.status} testid={`status-badge-project-${project.id}`} /></div>
            <div className="text-neutral-500">Priority</div><div className="font-mono-tight">{project.priority}</div>
            <div className="text-neutral-500">Manager</div>
            <div>
              {isSuperAdmin ? (
                <select data-testid="detail-reassign-manager" value={project.manager_id || ""} onChange={async (e) => {
                  try { await api.patch(`/projects/${id}`, { manager_id: e.target.value }); toast.success("Manager reassigned"); load(); }
                  catch (err) { toast.error(formatApiError(err)); }
                }} className="border border-black/15 rounded px-2 py-1 text-xs font-mono-tight bg-white max-w-full">
                  {!project.manager_id && <option value="">— unassigned —</option>}
                  {managers.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} {m.role === "SuperAdmin" ? "(SA)" : ""}</option>)}
                </select>
              ) : (
                <span className="font-mono-tight text-xs" data-testid="detail-manager-label">{project.manager_name || project.manager_email || "—"}</span>
              )}
            </div>
            <div className="text-neutral-500">Start</div><div className="font-mono-tight">{formatDate(project.start_date)}</div>
            <div className="text-neutral-500">Due</div><div className="font-mono-tight">{formatDate(project.due_date)}</div>
            <div className="text-neutral-500">Backup</div>
            <div className="font-mono-tight text-xs flex items-center gap-1" data-testid="detail-backup-manager">
              <Shield size={12} className="text-royal-700" />
              {project.backup_manager_name || <span className="text-neutral-400">— not set —</span>}
            </div>
            {project.service_name && (<><div className="text-neutral-500">Service</div><div className="text-xs" data-testid="overview-service">{project.service_name}</div></>)}
            {project.writer && (<><div className="text-neutral-500">Writer</div><div className="text-xs" data-testid="overview-writer">{project.writer}</div></>)}
            {project.reviewer && (<><div className="text-neutral-500">Reviewer</div><div className="text-xs" data-testid="overview-reviewer">{project.reviewer}</div></>)}
            {project.biostatistician && (<><div className="text-neutral-500">Biostatistician</div><div className="text-xs" data-testid="overview-biostat">{project.biostatistician}</div></>)}
            {project.uploader && (<><div className="text-neutral-500">Uploader</div><div className="text-xs" data-testid="overview-uploader">{project.uploader}</div></>)}
            {project.sme_id && (<><div className="text-neutral-500">SME</div><div className="text-xs" data-testid="overview-sme">{editingSmes.find((s) => s.id === project.sme_id)?.name || project.sme_id}</div></>)}
            {project.vendor_id && (<><div className="text-neutral-500">Vendor</div><div className="text-xs" data-testid="overview-vendor">{editingVendors.find((v) => v.id === project.vendor_id)?.company_name || project.vendor_id}</div></>)}
          </div>
          {(project.journals || []).some((j) => j?.name || j?.url) && (
            <div className="mt-4" data-testid="overview-journals">
              <div className="label-caps mb-1">Journals</div>
              <ul className="text-xs space-y-1">
                {(project.journals || []).filter((j) => j?.name || j?.url).map((j, i) => (
                  <li key={i} className="truncate">
                    <span className="text-neutral-500 font-mono-tight mr-1">J{i + 1} ·</span>
                    {j.url ? <a href={j.url} target="_blank" rel="noreferrer" className="underline underline-offset-4">{j.name || j.url}</a> : <span>{j.name}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-6 label-caps flex items-center gap-2 text-royal-800"><IndianRupee size={12} /> Budget vs Spend</div>
          <div className="mt-2 font-mono-tight text-2xl font-semibold">{formatINR(spent)} <span className="text-neutral-400 text-base">/ {formatINR(project.budget)}</span></div>
          <div className="mt-2 h-3 border border-black"><div className={`h-full ${budgetPct > 90 ? "bg-[#B02A00]" : "bg-royal-700"}`} style={{ width: `${budgetPct}%` }} /></div>
          <div className="mt-1 text-xs text-neutral-500 font-mono-tight" data-testid="budget-spent-summary">{budgetPct}% spent · {spend.invoices} invoice{spend.invoices === 1 ? "" : "s"} · Invoiced {formatINR(spend.invoiced || 0)}</div>
          </div>
        </div>

        <div className="border border-neutral-200 bg-white lg:col-span-2 flex flex-col gap-4">
          {isManager && (
            <div className="border border-black/10 bg-white rounded-xl" data-testid="client-details-box">
              <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between">
                <div className="label-caps text-royal-800">Client Details</div>
                <button data-testid="client-details-edit-btn" onClick={() => {
                  setClientDetailsDraft({
                    name: project.client_details?.name || project.client_name || "",
                    phone: project.client_details?.phone || "",
                    email: project.client_details?.email || "",
                    department: project.client_details?.department || "",
                    topic: project.client_details?.topic || "",
                  });
                  setClientDetailsOpen(true);
                }} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-royal-800 text-cream hover:brightness-110">
                  <Pencil size={11} /> {project.client_details ? "Edit" : "+ Add"}
                </button>
              </div>
              <div className="p-4 text-sm grid grid-cols-2 gap-y-2" data-testid="client-details-grid">
                <div className="text-neutral-500">Name</div><div>{project.client_details?.name || project.client_name || <span className="text-neutral-400">—</span>}</div>
                <div className="text-neutral-500">Phone</div><div className="font-mono-tight text-xs">{project.client_details?.phone || <span className="text-neutral-400">—</span>}</div>
                <div className="text-neutral-500">Email</div><div className="font-mono-tight text-xs">{project.client_details?.email || <span className="text-neutral-400">—</span>}</div>
                <div className="text-neutral-500">Department</div><div className="text-xs">{project.client_details?.department || <span className="text-neutral-400">—</span>}</div>
                <div className="text-neutral-500">Topic</div><div className="text-xs">{project.client_details?.topic || <span className="text-neutral-400">—</span>}</div>
              </div>
            </div>
          )}
          <div className="border border-neutral-200 bg-white rounded-xl flex-1">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps flex items-center gap-2"><Paperclip size={12} /> Attachments</div>
          <div className="p-4">
            {(isManager || isFreelancer) && (
              <div
                data-testid="dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed cursor-pointer text-center py-8 px-4 mb-4 transition-colors ${dragOver ? "border-[#002FA7] bg-[#F0F4FF]" : "border-black bg-[#FAFAFA] hover:bg-white"}`}
              >
                <UploadCloud size={28} className="mx-auto mb-2" strokeWidth={1.5} />
                <div className="font-medium">{uploading ? "Uploading…" : "Drag & drop files here"}</div>
                <div className="text-xs text-neutral-500 mt-1">or click to browse · up to 20 MB per file</div>
                <input ref={fileInputRef} data-testid="file-input" type="file" multiple hidden onChange={(e) => uploadFiles(e.target.files)} />
              </div>
            )}
            {(project.files || []).length === 0 && !isManager && !isFreelancer && <div className="text-sm text-neutral-500">No files attached yet.</div>}
            <ul className="space-y-2" data-testid="project-files">
              {(project.files || []).map((f) => {
                const isHidden = !!f.hidden;
                return (
                <li key={f.id} className={`flex items-center justify-between border rounded px-3 py-2 ${isHidden ? "border-neutral-300 bg-neutral-50" : "border-neutral-200"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {isHidden && <span data-testid={`file-hidden-badge-${f.id}`} className="text-[10px] font-mono-tight uppercase tracking-widest bg-neutral-800 text-cream px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Lock size={10} /> Hidden</span>}
                      {f.storage_path
                        ? <button data-testid={`file-download-${f.id}`} onClick={() => downloadStored(f)} className="underline underline-offset-4 text-sm truncate text-left">{f.name}</button>
                        : <a href={f.url} target="_blank" rel="noreferrer" className="underline underline-offset-4 text-sm truncate">{f.name}</a>}
                    </div>
                    <div className="text-xs text-neutral-500 font-mono-tight">
                      by {f.uploaded_by} · {formatDate(f.uploaded_at)}
                      {f.size ? ` · ${bytesLabel(f.size)}` : ""}
                      {f.url && !f.storage_path ? " · external link" : ""}
                      {isHidden && f.hidden_by ? ` · hidden by ${f.hidden_by}` : ""}
                    </div>
                  </div>
                  {isManager && (
                    <div className="flex items-center gap-2 ml-3">
                      <button data-testid={`file-hide-${f.id}`} onClick={() => toggleFileHidden(f)}
                        title={isHidden ? "Unhide — show to freelancers & clients" : "Hide from freelancers & clients"}
                        className={`p-1.5 rounded ${isHidden ? "hover:bg-royal-700/10 text-royal-700" : "hover:bg-neutral-100 text-neutral-500"}`}>
                        {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button data-testid={`remove-file-${f.id}`} onClick={() => removeFile(f.id)} className="text-[#FF2A00]"><Trash2 size={14} /></button>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          </div>
        </div>
        </div>

        <div className="border border-neutral-200 bg-white lg:col-span-3">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps flex items-center justify-between">
            <span>Work Orders</span>
            <label className="inline-flex items-center gap-2 text-xs normal-case tracking-normal cursor-pointer select-none" data-testid="pd-show-cancelled-label">
              <input data-testid="pd-show-cancelled-toggle" type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
              Show Cancelled ({orders.filter((o) => o.status === "Cancelled").length})
            </label>
          </div>
          <div className="overflow-x-auto">
          <table className="fms-table w-full" data-testid="pd-work-orders-table">
            <thead><tr><th>Title</th><th>Freelancer</th><th>Created</th><th>Issued By</th><th>Status</th><th>Rate</th><th>Deadline</th>{isManager && <th></th>}</tr></thead>
            <tbody>
              {orders.filter((o) => showCancelled || o.status !== "Cancelled").map((o) => {
                const isSubmitted = o.status === "Submitted" && isManager;
                return (
                <tr key={o.id} data-testid={`wo-${o.id}`} className={isSubmitted ? "bg-gold/10 border-l-4 border-gold" : o.status === "Cancelled" ? "opacity-60" : ""}>
                  <td>
                    {isSubmitted && <span data-testid={`wo-ready-flag-${o.id}`} className="mr-2 text-[10px] font-mono-tight uppercase tracking-widest bg-gold text-ink px-1.5 py-0.5 rounded">Ready to Review</span>}
                    <button type="button" data-testid={`open-wo-${o.id}`} onClick={() => setViewWoId(o.id)} className="text-left underline-offset-4 hover:underline font-medium">{o.title}</button>
                  </td>
                  <td className="text-sm">{fname(o.freelancer_id)}</td>
                  <td className="font-mono-tight text-xs text-neutral-600 whitespace-nowrap" data-testid={`pd-wo-created-${o.id}`}>{fmtCreated(o.created_at)}</td>
                  <td className="text-xs text-neutral-600" data-testid={`pd-wo-issued-by-${o.id}`}>{o.issued_by_name || "—"}</td>
                  <td className="font-mono-tight text-xs">{o.status}{o.reassign_count ? ` · ${o.reassign_count}×` : ""}</td>
                  <td className="font-mono-tight num">{formatINR(o.rate)}</td>
                  <td className="font-mono-tight text-neutral-500">{o.deadline || "—"}</td>
                  {isManager && (
                    <td className="whitespace-nowrap space-x-3">
                      <button data-testid={`edit-wo-${o.id}`} onClick={() => setEditingWo({ ...o })} className="text-xs underline underline-offset-4 inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                      <button data-testid={`delete-wo-${o.id}`} onClick={() => setDeletingWo(o)} className="text-xs underline underline-offset-4 text-red-600 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                    </td>
                  )}
                </tr>
                );
              })}
              {orders.length === 0 && <tr><td colSpan={isManager ? 8 : 7} className="text-center text-neutral-500 py-6">No orders in this project. {isManager && <button onClick={openConvertToWo} className="underline underline-offset-4 ml-2">+ New Work Order</button>}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>

        {isManager && (
          <div className="border border-black/10 bg-white rounded-2xl lg:col-span-3" data-testid="tickets-panel">
            <div className="px-4 py-3 border-b border-black/10 label-caps text-royal-800 flex items-center gap-2"><Ticket size={12} /> Project Tickets</div>
            <div className="p-4">
              {tickets.length === 0 ? (
                <div className="text-xs text-neutral-500">No tickets yet. Click "Create Ticket" to assign a quick task to a freelancer on this project.</div>
              ) : (
                <ul className="space-y-2">
                  {tickets.map((t) => {
                    const flName = freelancers.find((f) => f.id === t.freelancer_id)?.name || t.freelancer_id;
                    return (
                      <li key={t.id} className="border border-black/10 rounded-lg p-3" data-testid={`ticket-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{t.description}</div>
                            <div className="text-xs text-neutral-500 font-mono-tight mt-1 flex items-center gap-2 flex-wrap">
                              <span>{flName} · due {t.deadline || "—"}</span>
                              <StatusBadge status={t.closed ? "Closed" : t.status} testid={`status-badge-ticket-${t.id}`} />
                            </div>
                            {t.instructions && <div className="text-xs text-ink/70 mt-1 whitespace-pre-wrap">{t.instructions}</div>}
                            {t.response && <div className="text-xs mt-2 border-l-2 border-royal-700/30 pl-2"><b>Response:</b> {t.response}</div>}
                          </div>
                          {!t.closed && isManager && (
                            <button data-testid={`close-ticket-${t.id}`} onClick={() => updateTicket(t, { closed: true, status: "Closed" })} className="text-[10px] px-2 py-1 rounded bg-royal-800 text-cream">Close</button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {isFreelancer && tickets.length > 0 && (
          <div className="border border-black/10 bg-white rounded-2xl lg:col-span-3" data-testid="freelancer-tickets-panel">
            <div className="px-4 py-3 border-b border-black/10 label-caps text-royal-800 flex items-center gap-2"><Ticket size={12} /> Your Tickets on this Project</div>
            <div className="p-4 space-y-3">
              {tickets.map((t) => (
                <div key={t.id} className="border border-black/10 rounded-lg p-3" data-testid={`fl-ticket-${t.id}`}>
                  <div className="font-medium text-sm">{t.description}</div>
                  <div className="text-xs text-neutral-500 font-mono-tight mt-1 flex items-center gap-2 flex-wrap">
                    <span>Due {t.deadline || "—"}</span>
                    <StatusBadge status={t.status} testid={`status-badge-fl-ticket-${t.id}`} />
                  </div>
                  {t.instructions && <div className="text-xs text-ink/70 mt-1 whitespace-pre-wrap">Instructions: {t.instructions}</div>}
                  {!t.closed && (
                    <div className="mt-2 space-y-2">
                      <Textarea data-testid={`fl-ticket-response-${t.id}`} rows={2} placeholder="Write your response or completion note…" value={ticketResponse[t.id] || t.response || ""} onChange={(e) => setTicketResponse((s) => ({ ...s, [t.id]: e.target.value }))} className="rounded-lg border-black/15" />
                      <div className="flex gap-2">
                        <select data-testid={`fl-ticket-status-${t.id}`} value={t.status} onChange={(e) => updateTicket(t, { status: e.target.value, response: ticketResponse[t.id] ?? t.response })} className="h-8 border border-black/15 rounded px-2 text-xs bg-white">
                          {["Open", "In Progress", "Completed"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <button data-testid={`fl-ticket-save-${t.id}`} onClick={() => updateTicket(t, { response: ticketResponse[t.id] ?? t.response })} className="text-xs px-3 py-1 rounded bg-royal-800 text-cream">Save response</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isManager && (
          <div className="border border-black/10 bg-white rounded-2xl lg:col-span-3" data-testid="internal-notes-panel">
            <div className="px-4 py-3 border-b border-black/10 label-caps flex items-center gap-2 text-royal-800">
              <Lock size={12} /> Internal Notes
              <span className="text-ink/40">·</span>
              <span className="text-ink/60 normal-case tracking-normal">not visible to freelancers or clients</span>
            </div>
            <div className="p-4 space-y-3">
              <ul className="space-y-2" data-testid="internal-notes-list">
                {(project.internal_notes_log || []).slice().reverse().map((n) => (
                  <li key={n.id} className="border-l-2 border-royal-700 pl-3" data-testid={`internal-note-${n.id}`}>
                    <div className="text-xs text-neutral-500 font-mono-tight flex items-center gap-2">
                      <span data-testid={`note-timestamp-${n.id}`}>{formatNoteTs(n.created_at)}</span>
                      <span>— {n.author_name || "—"}</span>
                      <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="ml-auto text-[11px] underline underline-offset-4 text-[#B02A00]">Delete</button>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{n.text}</div>
                  </li>
                ))}
                {(!project.internal_notes_log || project.internal_notes_log.length === 0) && (
                  <li className="text-sm text-neutral-500">No notes yet.</li>
                )}
                {project.internal_notes && (
                  <li className="border-l-2 border-neutral-300 pl-3 bg-neutral-50 rounded" data-testid="legacy-internal-notes">
                    <div className="text-xs text-neutral-500 font-mono-tight">Legacy note</div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{project.internal_notes}</div>
                  </li>
                )}
              </ul>
              <Textarea data-testid="internal-notes-input" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Add a note — the timestamp is captured automatically."
                rows={3} className="rounded-lg border-black/15" />
              <div className="flex justify-end">
                <button data-testid="save-internal-notes" type="button" disabled={savingNotes || !(notesDraft || "").trim()} onClick={saveNotes} className="btn-primary disabled:opacity-50">
                  {savingNotes ? "Saving…" : "Add note"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isManager && (
          <div className="border border-black/10 bg-white rounded-2xl lg:col-span-3" data-testid="client-updates-panel">
            <div className="px-4 py-3 border-b border-black/10 label-caps flex items-center gap-2 text-royal-800"><Megaphone size={12} /> Client Updates <span className="text-ink/40">·</span> <span className="text-ink/60 normal-case tracking-normal">Posted to the client portal</span></div>
            <div className="p-4 space-y-3" data-testid="client-updates-thread">
              {clientUpdates.length === 0 && <div className="text-sm text-neutral-500">No client updates yet. Drafts you post here appear in the client's portal instantly.</div>}
              {clientUpdates.map((u) => (
                <div key={u.id} className="border-l-2 border-gold pl-3">
                  <div className="flex justify-between items-baseline">
                    <div className="text-sm font-semibold">{u.user_name} <span className="label-caps ml-2 text-royal-800">Client-Visible</span></div>
                    <div className="text-xs text-neutral-500 font-mono-tight">{formatDate(u.created_at)}</div>
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{u.body}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-black/10 p-3 space-y-2">
              <Textarea data-testid="client-update-input" value={newClientUpdate} onChange={(e) => setNewClientUpdate(e.target.value)} placeholder="Draft an update for the client…" rows={3} className="rounded-lg border-black/15" />
              <div className="flex justify-end">
                <button data-testid="post-client-update" type="button" disabled={postingUpdate || !newClientUpdate.trim()} onClick={postClientUpdate} className="btn-primary disabled:opacity-50 inline-flex items-center gap-2"><Send size={14} /> {postingUpdate ? "Posting…" : "Post to client"}</button>
              </div>
            </div>
          </div>
        )}


        <div className="border border-neutral-200 bg-white lg:col-span-3">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Project Chat</div>
          <div className="p-4 max-h-96 overflow-auto space-y-3" data-testid="project-comments">
            {comments.length === 0 && <div className="text-sm text-neutral-500">Start the conversation — comments are visible to the manager and every freelancer on this project.</div>}
            {comments.map((c) => (
              <div key={c.id} className="border-l-2 border-black pl-3">
                <div className="flex justify-between items-baseline">
                  <div className="text-sm font-semibold">{c.user_name} <span className="label-caps ml-2">{c.user_role}</span>
                    {c.client_visible && <span className="ml-2 text-[10px] font-mono-tight uppercase tracking-widest bg-gold text-ink px-1.5 py-0 rounded" data-testid={`comment-visible-${c.id}`}>Client</span>}
                  </div>
                  <div className="text-xs text-neutral-500 font-mono-tight">{formatDate(c.created_at)}</div>
                </div>
                <RichText html={c.body_html} text={c.body} testid={`comment-body-${c.id}`} className="text-sm mt-1" />
                {isManager && (
                  <button data-testid={`toggle-visible-${c.id}`} onClick={async () => { try { await api.patch(`/projects/${id}/comments/${c.id}/visibility?visible=${!c.client_visible}`); load(); } catch {} }}
                    className="text-[11px] underline underline-offset-2 mt-1 text-ink/60">{c.client_visible ? "Hide from client" : "Post as client update"}</button>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={submitComment} className="border-t border-neutral-200 p-3 flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1 min-w-0"><RichEditor ref={chatRef} testid="comment-input" value={newComment} onChange={setNewComment} placeholder="Write a message… (Ctrl+Enter to send)" minHeight={56} onSubmit={submitComment} /></div>
            <button data-testid="send-comment" type="submit" className="bg-black text-white px-4 py-2.5 flex items-center justify-center gap-2 text-sm"><Send size={14} /> Send</button>
          </form>
        </div>

        {isManager && <ClientTicketsPanel projectId={id} />}
        {isManager && <ClientApprovalPanel project={project} canUpload={isManager} onChanged={load} />}
        {isManager && <ProjectHistoryPanel history={project.history} />}
      </div>

      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">Convert to Work Order</DialogTitle></DialogHeader>
          <form onSubmit={submitWo} className="space-y-3">
            <div className="text-xs text-neutral-500 font-mono-tight">Project · {project.name}</div>
            <div>
              <label className="label-caps block mb-1">Freelancer</label>
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input data-testid="wo-freelancer-search" value={woSearch} onChange={(e) => setWoSearch(e.target.value)} placeholder="Search freelancer…" className="pl-8 rounded-none border-black h-9" />
              </div>
              <div className="border border-black mt-1 max-h-40 overflow-auto">
                {woFreelancers.length === 0 && <div className="p-2 text-xs text-neutral-500">No freelancers match your search.</div>}
                {woFreelancers.filter((f) => project.assigned_freelancer_ids.includes(f.id)).map((f) => (
                  <label key={f.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cream-2 ${woForm.freelancer_id === f.id ? "bg-royal-700/10" : ""}`}>
                    <input type="radio" name="wo-fl" value={f.id} checked={woForm.freelancer_id === f.id} onChange={() => setWoForm({ ...woForm, freelancer_id: f.id })} data-testid={`wo-fl-${f.id}`} />
                    <span>{f.name}</span><span className="text-xs text-neutral-500">· assigned</span>
                  </label>
                ))}
                {woFreelancers.filter((f) => !project.assigned_freelancer_ids.includes(f.id)).map((f) => (
                  <label key={f.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cream-2 ${woForm.freelancer_id === f.id ? "bg-royal-700/10" : ""}`}>
                    <input type="radio" name="wo-fl" value={f.id} checked={woForm.freelancer_id === f.id} onChange={() => setWoForm({ ...woForm, freelancer_id: f.id })} data-testid={`wo-fl-${f.id}`} />
                    <span>{f.name}</span><span className="text-xs text-neutral-500">· {f.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><label className="label-caps block mb-1">Title</label><Input required value={woForm.title} onChange={(e) => setWoForm({ ...woForm, title: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Description</label><RichEditor testid="wo-create-desc" value={woForm.description_html || ""} onChange={(h) => setWoForm({ ...woForm, description_html: h })} placeholder="Brief, steps, references…" minHeight={110} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Rate (INR)</label><Input required type="number" value={woForm.rate} onChange={(e) => setWoForm({ ...woForm, rate: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Deadline</label><Input type="date" value={woForm.deadline} onChange={(e) => setWoForm({ ...woForm, deadline: e.target.value })} className="rounded-none border-black" /></div>
            </div>
            <div><label className="label-caps block mb-1">Status</label>
              <select value={woForm.status} onChange={(e) => setWoForm({ ...woForm, status: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <DialogFooter><button data-testid="submit-convert-wo" type="submit" disabled={!woForm.freelancer_id} className="bg-black text-white px-4 py-2 text-sm disabled:opacity-50">Create Work Order</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">Attach Link</DialogTitle></DialogHeader>
          <form onSubmit={addLink} className="space-y-3">
            <div className="text-xs text-neutral-500">Attach an external link (Google Drive, Figma, Notion…). For real files, use the drop zone.</div>
            <div><label className="label-caps block mb-1">Name</label><Input data-testid="attach-file-name" required value={linkForm.name} onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">URL</label><Input data-testid="attach-file-url" required type="url" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} className="rounded-none border-black" /></div>
            <DialogFooter><button data-testid="submit-attach-link" type="submit" className="bg-black text-white px-4 py-2 text-sm">Attach</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-[1.25rem] max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit Project</DialogTitle></DialogHeader>
              <form onSubmit={submitEditProject} className="space-y-3">
                <div><label className="label-caps block mb-1">Name</label><Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div><label className="label-caps block mb-1">Client</label><Input required value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Status</label>
                    <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      {["Planned", "Active", "In Progress", "On Hold", "Completed"].map((s) => <option key={s}>{s}</option>)}
                    </select></div>
                  <div><label className="label-caps block mb-1">Priority</label>
                    <select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option>Low</option><option>Medium</option><option>High</option>
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Start</label><Input type="date" value={editing.start_date || ""} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                  <div><label className="label-caps block mb-1">Due</label><Input type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                </div>
                <div><label className="label-caps block mb-1">Budget (INR)</label><Input type="number" value={editing.budget} onChange={(e) => setEditing({ ...editing, budget: e.target.value })} className="rounded-lg border-black/15" /></div>
                <ProjectExtraFields value={editing} onChange={(v) => setEditing({ ...editing, ...v })} idPrefix="edit-detail" disabled={!isManager} freelancers={freelancers} serviceLocked={user.role === "Manager"} />
                {isSuperAdmin && (
                  <div><label className="label-caps block mb-1">Backup Manager (auto-covers leave)</label>
                    <select data-testid="edit-detail-backup" value={editing.backup_manager_id || ""} onChange={(e) => setEditing({ ...editing, backup_manager_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option value="">— none —</option>
                      {managers.filter((m) => m.id !== editing.manager_id).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}{m.role === "SuperAdmin" ? " (SA)" : m.role === "TeamLeader" ? " (TL)" : ""}</option>)}
                    </select>
                  </div>
                )}
                {isManager && (
                  <div><label className="label-caps block mb-1">Project Type (Vendor)</label>
                    <select data-testid="edit-detail-vendor" value={editing.vendor_id || ""} onChange={(e) => setEditing({ ...editing, vendor_id: e.target.value })} disabled={!isSuperAdmin}
                      title={isSuperAdmin ? "" : "Only Super Admins and Operation Heads can change the project type"}
                      className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">Non-Type</option>
                      {(editingVendors || []).map((v) => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                    </select>
                  </div>
                )}
                {isManager && (
                  <div><label className="label-caps block mb-1">SME</label>
                    <select data-testid="edit-detail-sme" value={editing.sme_id || ""} onChange={(e) => setEditing({ ...editing, sme_id: e.target.value })} disabled={!isSuperAdmin}
                      title={isSuperAdmin ? "" : "Only Super Admins and Operation Heads can change the SME"}
                      className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">— no SME —</option>
                      {(editingSmes || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div><label className="label-caps block mb-1">Service Template</label>
                  <select data-testid="edit-detail-service" value={editing.service_id || ""} disabled={user.role === "Manager"} title={user.role === "Manager" ? "Only Super Admin, Operation Head or Team Leader can change the Service" : ""} onChange={(e) => applyServiceTemplate(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-60 disabled:cursor-not-allowed">
                    <option value="">— keep current phases —</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {(s.phases || []).length}p / {(s.phases || []).reduce((a, p) => a + (p.days || 0), 0)}d</option>)}
                  </select>
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <label className="label-caps">Milestone Phases</label>
                    <button type="button" data-testid="add-detail-phase" onClick={() => setEditing((c) => ({ ...c, phases: [...(c.phases || []), { name: "", days: 1 }] }))} className="text-xs underline underline-offset-4">+ Add phase</button>
                  </div>
                  <div className="space-y-2" data-testid="detail-phase-editor">
                    {(editing.phases || []).map((p, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="font-mono-tight text-xs text-neutral-500 w-5">{idx + 1}.</div>
                        <Input required value={p.name} onChange={(e) => setEditing((c) => ({ ...c, phases: c.phases.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) }))} className="rounded-lg border-black/15 h-9 flex-1" placeholder="Phase name" data-testid={`detail-phase-name-${idx}`} />
                        <Input required type="number" min={1} value={p.days} onChange={(e) => setEditing((c) => ({ ...c, phases: c.phases.map((x, i) => i === idx ? { ...x, days: Number(e.target.value) || 0 } : x) }))} className="rounded-lg border-black/15 h-9 w-20" data-testid={`detail-phase-days-${idx}`} />
                        <span className="text-xs text-neutral-500 font-mono-tight">d</span>
                        <button type="button" data-testid={`detail-phase-remove-${idx}`} onClick={() => setEditing((c) => ({ ...c, phases: c.phases.filter((_, i) => i !== idx) }))} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    {(!editing.phases || editing.phases.length === 0) && (
                      <div className="text-xs text-neutral-500">No phases yet. Pick a Service Template above or click + Add phase.</div>
                    )}
                  </div>
                </div>
                <DialogFooter><button type="submit" data-testid="save-project-edit-detail" className="btn-primary">Save changes</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={clientDetailsOpen} onOpenChange={setClientDetailsOpen}>
        <DialogContent className="rounded-[1.25rem]" data-testid="client-details-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Client Details</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveClientDetails(clientDetailsDraft); }} className="space-y-3">
            <div><label className="label-caps block mb-1">Client Name</label>
              <Input data-testid="cd-name" value={clientDetailsDraft.name} onChange={(e) => setClientDetailsDraft({ ...clientDetailsDraft, name: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Phone / Contact</label>
                <Input data-testid="cd-phone" value={clientDetailsDraft.phone} onChange={(e) => setClientDetailsDraft({ ...clientDetailsDraft, phone: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Email</label>
                <Input data-testid="cd-email" type="email" value={clientDetailsDraft.email} onChange={(e) => setClientDetailsDraft({ ...clientDetailsDraft, email: e.target.value })} className="rounded-lg border-black/15" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Department</label>
                <Input data-testid="cd-department" value={clientDetailsDraft.department} onChange={(e) => setClientDetailsDraft({ ...clientDetailsDraft, department: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Topic</label>
                <Input data-testid="cd-topic" value={clientDetailsDraft.topic} onChange={(e) => setClientDetailsDraft({ ...clientDetailsDraft, topic: e.target.value })} className="rounded-lg border-black/15" /></div>
            </div>
            <DialogFooter><button type="submit" data-testid="cd-save" className="btn-primary">Save client details</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingWo} onOpenChange={(o) => { if (!o) { setEditingWo(null); setWoSearch(""); } }}>
        <DialogContent className="rounded-[1.25rem]" data-testid="edit-wo-dialog">
          {editingWo && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit Work Order</DialogTitle></DialogHeader>
              <form onSubmit={submitEditWo} className="space-y-3">
                <div><label className="label-caps block mb-1">Title</label><Input required value={editingWo.title} onChange={(e) => setEditingWo({ ...editingWo, title: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div><label className="label-caps block mb-1">Description</label><RichEditor testid="wo-edit-desc" value={editingWo.description_html ?? plainToHtml(editingWo.description)} onChange={(h) => setEditingWo({ ...editingWo, description_html: h })} minHeight={110} /></div>
                <div>
                  <label className="label-caps block mb-1">Freelancer</label>
                  <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="edit-wo-freelancer-search" value={woSearch} onChange={(e) => setWoSearch(e.target.value)} placeholder="Search freelancer…" className="pl-8 rounded-lg border-black/15 h-9" />
                  </div>
                  <div className="border border-black/15 rounded-lg mt-1 max-h-40 overflow-auto">
                    {woFreelancers.length === 0 && <div className="p-2 text-xs text-neutral-500">No freelancers match your search.</div>}
                    {woFreelancers.map((f) => (
                      <label key={f.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cream-2 ${editingWo.freelancer_id === f.id ? "bg-royal-700/10" : ""}`}>
                        <input type="radio" name="edit-wo-fl" value={f.id} checked={editingWo.freelancer_id === f.id} onChange={() => setEditingWo({ ...editingWo, freelancer_id: f.id })} data-testid={`edit-wo-fl-${f.id}`} />
                        <span>{f.name}</span><span className="text-xs text-neutral-500">· {f.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Rate (INR)</label><Input type="number" value={editingWo.rate} onChange={(e) => setEditingWo({ ...editingWo, rate: e.target.value })} className="rounded-lg border-black/15" /></div>
                  <div><label className="label-caps block mb-1">Deadline</label><Input type="date" value={editingWo.deadline || ""} onChange={(e) => setEditingWo({ ...editingWo, deadline: e.target.value })} className="rounded-lg border-black/15" /></div>
                </div>
                <div><label className="label-caps block mb-1">Status</label>
                  <select data-testid="edit-wo-status" value={editingWo.status} onChange={(e) => setEditingWo({ ...editingWo, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select></div>
                <DialogFooter><button type="submit" data-testid="save-wo-edit" disabled={woSaving} className="btn-primary disabled:opacity-50">{woSaving ? "Saving…" : "Save work order"}</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <WoPaymentDialog wo={paymentFor} onClose={() => setPaymentFor(null)} onDone={load} />
      <WoDetailDialog woId={viewWoId} onClose={() => setViewWoId(null)} />

      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="rounded-[1.25rem] max-w-lg" data-testid="create-ticket-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Create Ticket</DialogTitle></DialogHeader>
          <form onSubmit={submitTicket} className="space-y-3">
            <div>
              <label className="label-caps block mb-1">Assign to Freelancer</label>
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input data-testid="ticket-freelancer-search" value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} placeholder="Search freelancer…" className="pl-8 rounded-lg border-black/15 h-9" />
              </div>
              <div className="border border-black/15 rounded-lg mt-1 max-h-40 overflow-auto">
                {projectFreelancers.length === 0 && <div className="p-2 text-xs text-neutral-500">No freelancers on this project. Create a Work Order first.</div>}
                {projectFreelancers.map((f) => (
                  <label key={f.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cream-2 ${ticketForm.freelancer_id === f.id ? "bg-royal-700/10" : ""}`}>
                    <input type="radio" name="ticket-fl" value={f.id} checked={ticketForm.freelancer_id === f.id} onChange={() => setTicketForm({ ...ticketForm, freelancer_id: f.id })} data-testid={`ticket-fl-${f.id}`} />
                    <span>{f.name}</span><span className="text-xs text-neutral-500">· {f.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><label className="label-caps block mb-1">Ticket Description</label>
              <Input data-testid="ticket-description" required value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} className="rounded-lg border-black/15" />
            </div>
            <div><label className="label-caps block mb-1">Task Instructions</label>
              <Textarea data-testid="ticket-instructions" rows={3} value={ticketForm.instructions} onChange={(e) => setTicketForm({ ...ticketForm, instructions: e.target.value })} className="rounded-lg border-black/15" />
            </div>
            <div><label className="label-caps block mb-1">Deadline</label>
              <Input data-testid="ticket-deadline" type="date" value={ticketForm.deadline} onChange={(e) => setTicketForm({ ...ticketForm, deadline: e.target.value })} className="rounded-lg border-black/15" />
            </div>
            <DialogFooter>
              <button data-testid="ticket-submit" type="submit" disabled={!ticketForm.freelancer_id} className="btn-primary disabled:opacity-50">Create ticket</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletingWo} onOpenChange={(o) => !o && setDeletingWo(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="delete-wo-confirm-dialog">
          {deletingWo && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl text-red-600">Delete «{deletingWo.title}»?</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="text-ink/80">This is permanent. Any invoice linked to this work order will also be deleted.</div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-mono-tight">
                  ⚠ Deleting this work order will also remove its linked invoice{invoiceMap[deletingWo.id] ? ` (${invoiceMap[deletingWo.id].invoice_number})` : ""}. This cannot be undone.
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="delete-wo-detail-cancel" onClick={() => setDeletingWo(null)} className="flex-1 rounded-full border border-black/15 py-2.5 text-sm font-semibold hover:bg-cream-2">Keep work order</button>
                  <button data-testid="delete-wo-detail-confirm" onClick={confirmDeleteWo} className="flex-1 rounded-full bg-red-600 text-white py-2.5 text-sm font-semibold hover:bg-red-700">Yes, delete permanently</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
